/**
 * Featured football competitions shown first in league filters / lists
 * when they have fixtures (or offers). IDs match API-Sports `League.id`.
 */
export const FEATURED_LEAGUE_API_IDS = Object.freeze([
  39, // Premier League
  140, // La Liga
  78, // Bundesliga
  135, // Serie A
  61, // Ligue 1
  2, // UEFA Champions League
  3, // UEFA Europa League
]);

const FEATURED_NAME_RANK = Object.freeze({
  "premier league": 0,
  "la liga": 1,
  laliga: 1,
  bundesliga: 2,
  "serie a": 3,
  "ligue 1": 4,
  "uefa champions league": 5,
  "champions league": 5,
  "uefa europa league": 6,
  "europa league": 6,
});

const FEATURED_RANK = new Map(
  FEATURED_LEAGUE_API_IDS.map((id, index) => [Number(id), index])
);

const toFiniteNumber = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Resolve API-Sports league id from common league / option shapes. */
export function getLeagueApiId(league) {
  if (!league || typeof league !== "object") return null;
  return (
    toFiniteNumber(league.apiId) ??
    toFiniteNumber(league.id) ??
    toFiniteNumber(league.leagueId) ??
    null
  );
}

export function getLeagueDisplayName(league) {
  if (!league || typeof league !== "object") return "";
  return String(league.leagueName || league.name || league.league || "").trim();
}

/** Lower rank = higher priority. Returns null when not featured. */
export function getFeaturedLeagueRank(league) {
  const apiId = getLeagueApiId(league);
  if (apiId != null && FEATURED_RANK.has(apiId)) {
    return FEATURED_RANK.get(apiId);
  }
  const name = getLeagueDisplayName(league).toLowerCase();
  if (!name) return null;
  if (Object.prototype.hasOwnProperty.call(FEATURED_NAME_RANK, name)) {
    return FEATURED_NAME_RANK[name];
  }
  return null;
}

export function getLeagueItemCount(league, countKey = "count") {
  if (!league || typeof league !== "object") return 0;
  const raw =
    league[countKey] ??
    league.count ??
    league.fixtureCount ??
    league.offerCount ??
    league.marketCount ??
    0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Sort leagues: featured (with items) in fixed order, then others by count desc, then name.
 * Optional `hasLive` on items is used as a tie-breaker after featured rank / count.
 * Items with count 0 stay at the end (still sorted among themselves).
 */
export function sortLeaguesByPriority(leagues = [], options = {}) {
  const { countKey = "count" } = options;
  return [...(leagues || [])].sort((a, b) => {
    const countA = getLeagueItemCount(a, countKey);
    const countB = getLeagueItemCount(b, countKey);
    const featuredA = countA > 0 ? getFeaturedLeagueRank(a) : null;
    const featuredB = countB > 0 ? getFeaturedLeagueRank(b) : null;

    const aFeatured = featuredA != null;
    const bFeatured = featuredB != null;
    if (aFeatured !== bFeatured) return aFeatured ? -1 : 1;
    if (aFeatured && bFeatured && featuredA !== featuredB) {
      return featuredA - featuredB;
    }

    if (countA !== countB) return countB - countA;

    const liveA = a?.hasLive ? 1 : 0;
    const liveB = b?.hasLive ? 1 : 0;
    if (liveA !== liveB) return liveB - liveA;

    const nameA = getLeagueDisplayName(a);
    const nameB = getLeagueDisplayName(b);
    return nameA.localeCompare(nameB);
  });
}
